import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import type { EmergencyFundSection, FundGoalKey, FundOption } from './types';

/** Legacy capped `Current amount saved` alone at 999,999 while its own error copy said 9,999,999; the copy wins. */
export const MAX_FIELD_VALUE = 9999999;

export const RANGE_ERROR = 'Enter a number between 0 and 9,999,999';

/** Verbatim from the legacy page; it is longer than the Budget Calculator's. */
export const DISCLAIMER =
  'This calculator is for educational purposes only. It makes estimates based on the information you provide, and your ideal emergency fund amount may change if any of the information varies. You should enter figures that are appropriate to your individual situation. This calculator is not intended to offer any tax, legal, financial or investment advice.';

export const EXPENSE_FIELDS: readonly CalculatorField[] = [
  { label: 'Rent or mortgage payment', hint: 'Enter your monthly rent or mortgage payment.' },
  {
    label: 'Debt payments',
    hint: 'Enter how much you pay on credit cards and all loans (except your mortgage and car payment) every month.',
  },
  {
    label: 'Utility payments',
    hint: 'Enter the combined amount you pay for gas, water, electricity, garbage, or other utilities each month.',
  },
  {
    label: 'Phone & internet payments',
    hint: 'Enter the combined monthly total of your phone, internet, cable, streaming services, and any other similar payments.',
  },
  {
    label: 'Insurance payments',
    hint: 'Enter the combined monthly total of car, health, life, and home insurance payments as well as other insurance payments that aren’t deducted from your paycheck by your employer.',
  },
  {
    label: 'Transportation expenses',
    hint: 'Enter the combined monthly total of your car payment, fuel, and public transportation costs.',
  },
  { label: 'Grocery expenses', hint: 'Enter the combined monthly amount you spend on groceries.' },
  {
    label: 'Other expenses',
    hint: 'Enter the combined monthly total of any other expenses. Include the cost of prescription medications, gym memberships, clothes, etc.',
  },
];

export const MONTHLY_CONTRIBUTION_FIELD: CalculatorField = {
  label: 'Amount you can save each month',
  hint: 'Enter the amount of money you can comfortably save each month.',
};

export const CURRENT_SAVINGS_FIELD: CalculatorField = {
  label: 'Current amount saved',
  hint: 'Enter the total amount of money you’ve already saved for your emergency fund.',
};

/** Only the expense section feeds the monthly total the goal is a multiple of. */
export const EMERGENCY_FUND_SECTIONS: readonly EmergencyFundSection[] = [
  { key: 'expenses', legend: 'Monthly expenses', fields: EXPENSE_FIELDS },
  { key: 'savings', legend: 'Your savings', fields: [MONTHLY_CONTRIBUTION_FIELD, CURRENT_SAVINGS_FIELD] },
];

export const EMERGENCY_FUND_FIELDS: readonly CalculatorField[] = EMERGENCY_FUND_SECTIONS.flatMap(
  section => section.fields,
);

/** Start empty so both calculators share the same input behavior. */
export const INITIAL_VALUES: CalculatorFieldValues = Object.fromEntries(
  EMERGENCY_FUND_FIELDS.map(field => [field.label, '']),
);

const FUND_GOAL_KEYS: readonly FundGoalKey[] = [3, 4, 5, 6, 7, 8, 9, 12];

export const fundLabel = (goalKey: FundGoalKey): string => `${goalKey}-month fund`;

export const FUND_OPTIONS: readonly FundOption[] = FUND_GOAL_KEYS.map(key => ({ key, label: fundLabel(key) }));

/** Legacy re-selected the first option on every calculation rather than keeping the last choice. */
export const DEFAULT_GOAL_KEY: FundGoalKey = 3;

/** The label alone reads as the sentence the picker completes. */
export const DURATION_FIELD: CalculatorField = {
  label: 'To create a',
  hint: 'Choose how many months of expenses you’d like your emergency fund to cover.',
};

export const RESULT_LABELS = {
  monthlyExpenses: 'Monthly expenses total:',
  monthlyContribution: 'If you continue to save',
  goal: 'You’ll need to save',
  monthsToGoal: 'Time to reach emergency fund goal',
} as const;

/** Self-contained copy for the live-region announcement. */
export const SUMMARY_COPY = {
  goal: (goal: string, goalMonths: string) =>
    `You’ll need to save ${goal} for emergencies — this represents ${goalMonths} of basic living expenses.`,
  pace: (monthsToGoal: string) => `At your current pace, you’ll reach your savings goal in ${monthsToGoal}.`,
  alreadyFunded: (goalMonths: FundGoalKey) =>
    `Congrats! You already have enough saved for your ${goalMonths}-month fund.`,
} as const;

/** Legacy showed this wherever a zero contribution left the timeline undefined. */
export const NO_TIMELINE = '-';

export const GOAL_REACHED = 'Goal reached';
